import { useState } from 'react';

import { Button, Indicator, Modal } from 'components/common';

type DeleteRuleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  ruleName: string;
};

export const DeleteRuleModal = ({ isOpen, onClose, onConfirm, ruleName }: DeleteRuleModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsLoading(true);
    setErrorText(null);

    try {
      await onConfirm();
      onClose();
    } catch (error) {
      setErrorText('Failed to delete conversion rule. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setErrorText(null);
    onClose();
  };

  return (
    <Modal open={isOpen} onCancel={handleClose} footer={null} closable={false} width={420}>
      <div className="w-full bg-white rounded-xl flex-col justify-start items-start inline-flex">
        {/* Header */}
        <div className="self-stretch px-6 justify-start items-start gap-6 inline-flex">
          <div className="grow shrink basis-0 py-5 flex-col justify-start items-start gap-2 inline-flex">
            <h1 className="self-stretch text-base-md font-medium leading-snug text-secondary-black">
              Delete rule
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pt-3 gap-6 stretch-to-form">
          <div className="text-secondary-black text-base-sm font-medium leading-tight">
            Are you sure you want to permanently delete the rule &quot;{ruleName}&quot;?
          </div>
          <div className="stretch-to-form gap-3 mt-3">
            {errorText && (
              <Indicator variant="danger" size="lg">
                {errorText}
              </Indicator>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="self-stretch h-16 px-6 border-t border-primary-gray-100 justify-end items-center gap-2 inline-flex mt-4">
          <div className="justify-end items-center gap-2 flex">
            <Button
              size="md"
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
              className="w-20"
            >
              Cancel
            </Button>
            <Button size="md" variant="danger" onClick={handleDelete} isLoading={isLoading}>
              Delete
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
